// Import CSV utilities
import { parseOfferData, parseFeedbackData } from './csvParser';
import { transformToCandidatesData, transformToNPSData, transformToSalaryData, transformToWeeklyReportsData } from './dataTransformer';

// Configuration - Now prioritizing Google Sheets over CSV
const USE_GOOGLE_SHEETS = true; // Set to true to use Google Sheets as primary data source
const USE_CSV_FALLBACK = true; // Keep CSV as fallback option

// Google Sheets configuration
const GOOGLE_SHEET_ID_MAIN = process.env.REACT_APP_GOOGLE_SHEET_ID_MAIN || '1IodpCHbsCBjQQ9vNU-8A9LJ9_6bJ6UG8D5G3xWnasgc';
const GOOGLE_SHEET_ID_NPS = process.env.REACT_APP_GOOGLE_SHEET_ID_NPS || '16v4pgw6PQlt5E3TXre4oROkjXrs0VTojKFsWqbv7fRc';
const GOOGLE_SHEET_ID_WEEKLY = process.env.REACT_APP_GOOGLE_SHEET_ID_WEEKLY || '1xtfTS2R6KaGdOqBrtjALM5HaKAoy_QDUBbSqrJvUha4';
// Backward compatibility
const GOOGLE_SHEET_ID = GOOGLE_SHEET_ID_MAIN;

// API endpoints (local development vs production)
const isProduction = process.env.NODE_ENV === 'production';
const PROXY_URL = isProduction ? '/.netlify/functions/proxy' : process.env.REACT_APP_PROXY_URL || 'http://localhost:3001/api/proxy';
// Data fetch proxy: uses legacy route in dev (accepts type+sheet_id), netlify function in prod
const DATA_PROXY_URL = isProduction ? '/.netlify/functions/proxy' : 'http://localhost:3001/api/proxy-legacy';

/**
 * Fetch data from either local CSV files or through the proxy server
 * @param {string} type - The type of data to fetch (candidates, nps, salary)
 * @returns {Promise<Array>} The data from the CSV or API
 */
export const fetchData = async (type) => {
  // Primary: Try Google Sheets first if enabled
  if (USE_GOOGLE_SHEETS) {
    try {
      // Determine the correct sheet ID based on data type
      const sheetId = (type === 'nps') ? GOOGLE_SHEET_ID_NPS : (type === 'weekly-reports') ? GOOGLE_SHEET_ID_WEEKLY : GOOGLE_SHEET_ID_MAIN;

      console.log(`Fetching ${type} data from Google Sheets via proxy (Sheet ID: ${sheetId})...`);

      // Route through proxy (dev: server.js, prod: netlify function) to avoid CORS
      const proxyUrl = `${DATA_PROXY_URL}?type=${type}&sheet_id=${sheetId}`;
      console.log(`Fetching via proxy: ${proxyUrl}`);

      const response = await fetch(proxyUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const result = await response.json();
      console.log(`${type} data result from Google Sheets:`, result);
      
      if (result.success && result.data && result.data.length > 0) {
        // Transform the data based on type
        switch(type) {
          case 'candidates':
            return transformToCandidatesData(result.data);
          case 'nps':
            return transformToNPSData(result.data);
          case 'salary':
            return transformToSalaryData(result.data);
          case 'weekly-reports':
            return transformToWeeklyReportsData(result.data);
          default:
            return result.data;
        }
      } else {
        throw new Error(result.message || result.error || `No data returned from Google Sheets for ${type}`);
      }
    } catch (sheetsError) {
      console.error(`Error fetching ${type} data from Google Sheets:`, sheetsError);
      console.log('Falling back to CSV files...');
      // Fall back to CSV if Google Sheets fails
    }
  }
  
  // Fallback: Try local CSV files if Google Sheets fails or is disabled
  if (USE_CSV_FALLBACK) {
    try {
      console.log(`Fetching ${type} data from local CSV files (fallback)...`);
      
      switch(type) {
        case 'candidates':
          const offerData = await parseOfferData();
          return transformToCandidatesData(offerData);
        
        case 'nps':
          const feedbackData = await parseFeedbackData();
          return transformToNPSData(feedbackData);
        
        case 'salary':
          const salaryOfferData = await parseOfferData();
          return transformToSalaryData(salaryOfferData);

        case 'weekly-reports':
          // No CSV fallback for weekly reports — return empty array
          console.warn('No CSV fallback available for weekly-reports');
          return [];

        default:
          throw new Error(`Unknown data type: ${type}`);
      }
    } catch (csvError) {
      console.error(`Error fetching ${type} data from CSV fallback:`, csvError);
      console.log('All data sources failed, returning empty array');
      throw new Error(`Failed to fetch ${type} data from both Google Sheets and CSV sources`);
    }
  }
  
  // If both are disabled, throw error
  throw new Error(`No data sources enabled for ${type} data`);
};

/**
 * Function to handle Google Apps Script authorization
 * @param {string} scriptUrl - The URL of the Google Apps Script
 * @param {string} type - The type of data to fetch
 */
export const authorizeAndAccessScript = (scriptUrl, type) => {
  // Open the script URL in a new tab for authorization
  window.open(scriptUrl + '?type=' + type, '_blank');
  
  // Show a message to the user
  alert('Please authorize the Google Apps Script in the new tab. After authorizing, close that tab and click the "Reload Dashboard" button.');
};


/**
 * Function to handle a proxy request using fetch with proxy
 * @param {string} baseUrl - The base URL to proxy to
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} The fetch response
 */
export const fetchWithProxy = async (baseUrl, options = {}) => {
  const proxyUrl = PROXY_URL;
  return fetch(proxyUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      url: baseUrl,
      ...options
    }),
  });
};

/**
 * Fetch open roles data
 * @returns {Promise<Array>} Array of open roles
 */
export const getOpenRoles = async () => {
  try {
    const isProduction = process.env.NODE_ENV === 'production';
    
    // In production, use the proxy function with action=openRoles
    // In development, use the local server endpoint
    const apiUrl = isProduction 
      ? '/.netlify/functions/proxy?action=openRoles' 
      : process.env.REACT_APP_OPEN_ROLES_URL || 'http://localhost:3001/api/open-roles';
    
    console.log('Fetching open roles from:', apiUrl);
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    
    const result = await response.json();
    console.log('Open roles API result:', result);
    
    // Handle both proxy response format and direct API response format
    if (result.ok && Array.isArray(result.data)) {
      return result.data;
    } else if (result.success && Array.isArray(result.data)) {
      // Handle Google Apps Script response format
      return result.data;
    } else {
      throw new Error(result.error || result.message || 'Failed to fetch open roles data');
    }
  } catch (error) {
    console.error('Error fetching open roles:', error);
    throw error;
  }
};

/**
 * Test Google Apps Script connectivity
 * @param {string} scriptUrl - The URL of the Google Apps Script
 * @returns {Promise<boolean>} True if successful
 */
export const testGoogleAppsScript = async (scriptUrl) => {
  try {
    const response = await fetch(`${scriptUrl}?type=test`);
    const data = await response.json();
    return data.success === true;
  } catch (error) {
    console.error('Failed to test Google Apps Script:', error);
    return false;
  }
};
